import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { HotelProvider } from './contexts/HotelContext'
import { NotificationsProvider } from './contexts/NotificationsContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AdminDebugPanel } from './components/AdminDebugPanel'
import { DailyChecklistReminder } from './components/DailyChecklistReminder'
import { useActivityTracker } from './hooks/useActivityTracker'

// Activity Tracking Component
function ActivityTracker() {
  useActivityTracker();
  return null;
}

// Lazy load all pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })))
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage').then(m => ({ default: m.ConfirmEmailPage })))
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const AdminCenter = lazy(() => import('./pages/AdminCenter').then(m => ({ default: m.AdminCenter })))
const AccountsReceivableHub = lazy(() => import('./pages/accountsreceivable/AccountsReceivableHub').then(m => ({ default: m.AccountsReceivableHub })))
const ClientsPage = lazy(() => import('./pages/accountsreceivable/clients').then(m => ({ default: m.ClientsPage })))
const CompaniesPage = lazy(() => import('./pages/accountsreceivable/companies').then(m => ({ default: m.CompaniesPage })))
const ExpensesPage = lazy(() => import('./pages/ExpensesPage').then(m => ({ default: m.ExpensesPage })))
const VendorsPage = lazy(() => import('./pages/VendorsPage').then(m => ({ default: m.VendorsPage })))
const DNRGuests = lazy(() => import('./pages/DNRGuests').then(m => ({ default: m.DNRGuests })))
const ParkingPermits = lazy(() => import('./pages/ParkingPermits').then(m => ({ default: m.ParkingPermits })))
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const BadDebtLog = lazy(() => import('./pages/BadDebtLog').then(m => ({ default: m.BadDebtLog })))
const EvictionsLog = lazy(() => import('./pages/EvictionsLog').then(m => ({ default: m.EvictionsLog })))
const InspectionsPage = lazy(() => import('./pages/InspectionsPage').then(m => ({ default: m.InspectionsPage })))
const FrontDeskActivities = lazy(() => import('./pages/FrontDeskActivities').then(m => ({ default: m.FrontDeskActivities })))
const HousekeepingActivities = lazy(() => import('./pages/HousekeepingActivities').then(m => ({ default: m.HousekeepingActivities })))
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })))
const PaymentsPage = lazy(() => import('./pages/PaymentsPage').then(m => ({ default: m.PaymentsPage })))
const AgingReportPage = lazy(() => import('./pages/AgingReportPage').then(m => ({ default: m.AgingReportPage })))
const HotelSelectionPage = lazy(() => import('./pages/HotelSelectionPage').then(m => ({ default: m.HotelSelectionPage })))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })))
const StudioViewerPage = lazy(() => import('./pages/StudioViewerPage').then(m => ({ default: m.StudioViewerPage })))
const RoomManagementPage = lazy(() => import('./pages/RoomManagementPage').then(m => ({ default: m.RoomManagementPage })))
const InventoryManagement = lazy(() => import('./pages/InventoryManagement').then(m => ({ default: m.InventoryManagement })))

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
)

function App() {
  return (
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <HotelProvider>
              <NotificationsProvider>
                <ActivityTracker />
                <AdminDebugPanel />
                <DailyChecklistReminder />
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/confirm-email" element={<ConfirmEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/studio-viewer/:slug" element={<StudioViewerPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              
              {/* Protected routes */}
              <Route path="/onboarding" element={
                <ProtectedRoute authOnly>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute feature="admin">
                  <AdminCenter />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute feature="notifications">
                  <NotificationsPage />
                </ProtectedRoute>
              } />
              <Route path="/accountsreceivable" element={
                <ProtectedRoute feature="accounts-receivable">
                  <AccountsReceivableHub />
                </ProtectedRoute>
              } />
              <Route path="/accountsreceivable/payments" element={
                <ProtectedRoute feature="accounts-receivable">
                  <PaymentsPage />
                </ProtectedRoute>
              } />
              <Route path="/accountsreceivable/invoices" element={
                <ProtectedRoute feature="accounts-receivable">
                  <InvoicesPage />
                </ProtectedRoute>
              } />
              <Route path="/accountsreceivable/aging" element={
                <ProtectedRoute feature="accounts-receivable">
                  <AgingReportPage />
                </ProtectedRoute>
              } />
              <Route path="/accountsreceivable/clients" element={
                <ProtectedRoute feature="accounts-receivable">
                  <ClientsPage />
                </ProtectedRoute>
              } />
              <Route path="/accountsreceivable/companies" element={
                <ProtectedRoute feature="accounts-receivable">
                  <CompaniesPage />
                </ProtectedRoute>
              } />
              <Route path="/expenses" element={
                <ProtectedRoute feature="expenses">
                  <ExpensesPage />
                </ProtectedRoute>
              } />
              <Route path="/vendors" element={
                <ProtectedRoute feature="expenses">
                  <VendorsPage />
                </ProtectedRoute>
              } />
              <Route path="/dnr" element={
                <ProtectedRoute feature="dnr">
                  <DNRGuests />
                </ProtectedRoute>
              } />
              <Route path="/parking-permits" element={
                <ProtectedRoute feature="parking-permits">
                  <ParkingPermits />
                </ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute feature="projects">
                  <ProjectsPage />
                </ProtectedRoute>
              } />
              <Route path="/bad-debt" element={
                <ProtectedRoute feature="bad-debt">
                  <BadDebtLog />
                </ProtectedRoute>
              } />
              <Route path="/evictions" element={
                <ProtectedRoute feature="evictions">
                  <EvictionsLog />
                </ProtectedRoute>
              } />
              <Route path="/inspections" element={
                <ProtectedRoute feature="inspections">
                  <InspectionsPage />
                </ProtectedRoute>
              } />
              <Route path="/inventory-management" element={
                <ProtectedRoute feature="inventory">
                  <InventoryManagement />
                </ProtectedRoute>
              } />
              <Route path="/front-desk" element={
                <ProtectedRoute feature="front-desk">
                  <FrontDeskActivities />
                </ProtectedRoute>
              } />
              <Route path="/housekeeping" element={
                <ProtectedRoute feature="housekeeping">
                  <HousekeepingActivities />
                </ProtectedRoute>
              } />
              <Route path="/hotel-selection" element={
                <ProtectedRoute>
                  <HotelSelectionPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <AccountSettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/studio-management" element={
                <ProtectedRoute feature="studio-management">
                  <RoomManagementPage />
                </ProtectedRoute>
              } />
              
              {/* Catch all route - redirect to dashboard instead of landing page */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </Suspense>
              </div>
              <SpeedInsights />
              </NotificationsProvider>
            </HotelProvider>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
  )
}

export default App
